import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const avatarWithoutClientsArticle: BlogArticle = {
  key: BlogArticleKey.AVATAR_WITHOUT_CLIENTS,
  slug: BlogSlugs.AVATAR_WITHOUT_CLIENTS,
  categoryId: "02",
  date: "2026-08-30",
  readTime: 7,
  locales: {
    fr: {
      title: "Définir son client idéal quand on n'a pas encore de clients",
      excerpt:
        "Le conseil habituel est de partir de ses clients actuels. Quand il n'y en a pas, la plupart des gens inventent un persona. Il existe une troisième voie, et elle prend une semaine.",
      description:
        "Comment construire un avatar client sans base de clients : par où commencer, combien de conversations suffisent, et comment éviter le persona inventé.",
      ctaTitle: "Transforme tes premières conversations en avatar",
      ctaText:
        "Dès le premier appel, on en tire la situation, les objections et les mots employés. Ton avatar se construit pendant que tu cherches tes premiers clients. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tous les guides disent la même chose : pour définir ton client idéal, regarde tes meilleurs clients. C'est un excellent conseil, et il est inutilisable pour la personne qui en a le plus besoin.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Quand on n'a pas encore vendu, il ne reste que deux options apparentes. Attendre, et prospecter à l'aveugle en attendant. Ou inventer un persona, avec son prénom, son âge et ses centres d'intérêt, qui décrira très précisément quelqu'un qui n'existe pas.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Un persona inventé n'est pas une hypothèse, c'est une décoration. Une hypothèse, ça se formule pour être démentie.",
        },
        { type: ArticleBlockType.H2, text: "Ce qui remplace les clients" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Il te manque des clients, pas des conversations. Et c'est la conversation qui produit la matière, pas la transaction. Cinq à dix échanges avec des gens qui vivent le problème valent plus qu'un atelier de positionnement, et coûtent une semaine.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La difficulté n'est pas d'obtenir ces conversations, elle est de ne pas les transformer en démonstrations. Quelqu'un qui accepte de te parler de sa situation cesse de le faire dès que tu commences à vendre.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce que tu n'as pas", "Ce qui le remplace"],
          rows: [
            ["Des clients signés", "Cinq à dix conversations sur la situation"],
            ["Un historique de vente", "Ce qu'ils ont déjà essayé et abandonné"],
            ["Des objections entendues", "Les raisons pour lesquelles ils n'ont rien fait"],
            ["Un vocabulaire client", "Leurs mots, notés tels quels"],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La deuxième ligne est la plus sous-estimée. Ce que quelqu'un a déjà essayé, et pourquoi il a arrêté, en dit plus sur son problème que n'importe quelle description. C'est aussi ce contre quoi ton offre sera comparée.",
        },
        { type: ArticleBlockType.H2, text: "Par où commencer, concrètement" },
        { type: ArticleBlockType.H3, text: "1. Écris une hypothèse en une phrase" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Pas une fiche, une phrase, et une phrase qui peut se révéler fausse. *Les indépendants qui vendent au téléphone perdent des affaires parce qu'ils ne relisent jamais leurs échanges.* Ça se dément en cinq conversations, et c'est tout ce qu'on lui demande.",
        },
        { type: ArticleBlockType.H3, text: "2. Trouve dix personnes qui pourraient la démentir" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Pas dix personnes gentilles, dix personnes concernées. Ton réseau en contient déjà, et c'est le seul endroit où tu obtiendras une réponse rapidement. Demande une conversation de vingt minutes sur leur façon de travailler, sans rien proposer.",
        },
        { type: ArticleBlockType.H3, text: "3. Écoute la situation, pas l'avis" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "*Est-ce que tu paierais pour ça ?* ne vaut rien, tout le monde répond oui par politesse. *Comment tu fais aujourd'hui ?* et *raconte-moi la dernière fois* valent tout, parce qu'ils font décrire des faits.",
        },
        { type: ArticleBlockType.H3, text: "4. Note les mots, pas ton résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "C'est le point que presque personne ne tient. Reformuler avec tes mots détruit exactement ce que tu es venu chercher. Garde les phrases telles quelles, elles serviront ensuite dans ton offre et dans tes messages.",
        },
        { type: ArticleBlockType.H2, text: "Ce qui distingue une hypothèse d'un persona" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Le persona inventé",
            items: [
              "Un prénom, un âge, une photo",
              "Des centres d'intérêt qui ne servent à rien",
              "Aucune ligne qui pourrait se révéler fausse",
              "Écrit une fois, jamais rouvert",
            ],
          },
          good: {
            title: "L'hypothèse testable",
            items: [
              "Une situation, un déclencheur, une conséquence",
              "Des critères qu'on tranche par oui ou non",
              "Des lignes qui peuvent être démenties",
              "Corrigée après chaque conversation",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La différence tient au dernier point. Un persona est un livrable, une hypothèse est un état provisoire. Si ton avatar n'a pas changé après dix conversations, ce n'est pas qu'il était juste, c'est que tu ne l'as pas confronté.",
        },
        { type: ArticleBlockType.H2, text: "Quand s'arrêter d'écouter" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Il n'y a pas de nombre magique, il y a un signal : quand tu peux prédire ce que la personne va dire avant qu'elle le dise, tu as assez écouté. Ça arrive souvent autour de la huitième conversation, parfois avant.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "À l'inverse, si chaque échange t'apprend quelque chose de neuf après quinze conversations, ce n'est pas bon signe : tu parles probablement à des gens trop différents, et ton hypothèse est trop large pour être testée.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Et le jour où tu signes tes premiers clients, tout ce travail ne devient pas caduc. Il devient comparable : tu confrontes ton hypothèse à ce que les gens qui ont payé racontent, et l'écart entre les deux est l'information la plus précieuse de tes six premiers mois.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "Tu n'as pas besoin d'un historique pour commencer. Dès la première conversation enregistrée, on en sort la situation décrite, ce qui a déjà été tenté, les objections et les formulations exactes. Ton avatar se construit pendant la phase de recherche, et se corrige à chaque nouvel appel au lieu d'être écrit une fois.",
          linkLabel: "Découvrir l'avatar client",
          linkPath: ProtectedPaths.CLIENT_AVATAR,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "N'attends pas d'avoir des clients, et n'invente pas de persona. Écris une phrase qui peut être fausse, va la confronter à dix personnes concernées, fais-leur décrire des faits plutôt que des avis, et garde leurs mots. En une semaine tu as davantage qu'un atelier, et surtout quelque chose qui se corrige.",
        },
      ],
      faq: [
        {
          question: "Comment définir son client idéal sans clients ?",
          answer:
            "En remplaçant les clients par des conversations. Écris une hypothèse en une phrase qui peut se révéler fausse, va la confronter à cinq ou dix personnes concernées, et fais-leur décrire leur façon de travailler plutôt que leur avis sur ton idée.",
        },
        {
          question: "Combien de conversations faut-il pour valider un avatar ?",
          answer:
            "Il n'y a pas de nombre fixe, il y a un signal : quand tu prédis ce que la personne va dire avant qu'elle le dise, tu as assez écouté. Cela arrive souvent autour de la huitième. Si tout est encore nouveau après quinze, ton hypothèse est trop large.",
        },
        {
          question: "Un persona marketing est-il utile au démarrage ?",
          answer:
            "Rarement. Un persona inventé avec un prénom, un âge et des centres d'intérêt ne contient aucune ligne qui puisse se révéler fausse, donc il ne peut rien t'apprendre. Une hypothèse formulée pour être démentie remplit le même rôle et progresse.",
        },
        {
          question: "Quelles questions poser lors de ces conversations ?",
          answer:
            "Des questions qui font décrire des faits : comment tu fais aujourd'hui, raconte-moi la dernière fois, qu'est-ce que tu as déjà essayé et pourquoi tu as arrêté. Évite « est-ce que tu paierais pour ça », à laquelle tout le monde répond oui par politesse.",
        },
        {
          question: "Que faire de ce travail une fois les premiers clients signés ?",
          answer:
            "Le comparer. Confronte ton hypothèse de départ à ce que racontent les gens qui ont réellement payé. L'écart entre les deux est l'information la plus utile de tes premiers mois, et il désigne précisément ce que tu avais mal compris.",
        },
      ],
    },
    en: {
      title: "Defining your ideal customer before you have any customers",
      excerpt:
        "The usual advice is to start from your current customers. With none, most people invent a persona. There is a third route, and it takes a week.",
      description:
        "How to build a client avatar with no customer base: where to start, how many conversations are enough, and how to avoid the invented persona.",
      ctaTitle: "Turn your first conversations into an avatar",
      ctaText:
        "From the very first call we pull out the situation, the objections and the words used. Your avatar builds itself while you look for your first customers. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Every guide says the same thing: to define your ideal customer, look at your best customers. It is excellent advice, and useless to the person who needs it most.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "With nothing sold yet, two options appear to remain. Wait, and prospect blind in the meantime. Or invent a persona, with a first name, an age and hobbies, describing very precisely someone who does not exist.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "An invented persona is not a hypothesis, it is decoration. A hypothesis is phrased so it can be proven wrong.",
        },
        { type: ArticleBlockType.H2, text: "What replaces customers" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "What you lack is customers, not conversations. And it is the conversation that produces the material, not the transaction. Five to ten exchanges with people living the problem are worth more than a positioning workshop, and cost a week.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The difficulty is not getting those conversations, it is not turning them into demos. Someone willing to describe their situation stops the moment you start selling.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["What you do not have", "What replaces it"],
          rows: [
            ["Signed customers", "Five to ten conversations about the situation"],
            ["A sales history", "What they already tried and abandoned"],
            ["Objections heard", "The reasons they did nothing about it"],
            ["A customer vocabulary", "Their words, written down as spoken"],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The second row is the most underrated. What someone already tried, and why they stopped, says more about their problem than any description. It is also what your offer will be compared against.",
        },
        { type: ArticleBlockType.H2, text: "Where to start, concretely" },
        { type: ArticleBlockType.H3, text: "1. Write a one-sentence hypothesis" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Not a profile sheet, a sentence, and one that could turn out false. *Independents who sell over the phone lose deals because they never read their conversations back.* That can be disproved in five conversations, and that is all that is asked of it.",
        },
        { type: ArticleBlockType.H3, text: "2. Find ten people who could disprove it" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Not ten kind people, ten concerned ones. Your network already holds some, and it is the only place you will get an answer quickly. Ask for a twenty-minute conversation about how they work, with nothing proposed.",
        },
        { type: ArticleBlockType.H3, text: "3. Listen for the situation, not the opinion" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "*Would you pay for this?* is worthless, everyone says yes out of politeness. *How do you do it today?* and *tell me about the last time* are worth everything, because they make people describe facts.",
        },
        { type: ArticleBlockType.H3, text: "4. Record the words, not your summary" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "This is the point almost nobody holds. Rewording in your own language destroys exactly what you came for. Keep the sentences as spoken; they will serve later in your offer and your messages.",
        },
        { type: ArticleBlockType.H2, text: "What separates a hypothesis from a persona" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "The invented persona",
            items: [
              "A first name, an age, a photo",
              "Hobbies that serve no purpose",
              "Not one line that could turn out false",
              "Written once, never reopened",
            ],
          },
          good: {
            title: "The testable hypothesis",
            items: [
              "A situation, a trigger, a consequence",
              "Criteria settled by yes or no",
              "Lines that can be disproved",
              "Corrected after every conversation",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The difference is the last point. A persona is a deliverable, a hypothesis is a temporary state. If your avatar has not changed after ten conversations, it is not that it was right, it is that you never confronted it.",
        },
        { type: ArticleBlockType.H2, text: "When to stop listening" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "There is no magic number, there is a signal: when you can predict what the person is about to say before they say it, you have listened enough. It often arrives around the eighth conversation, sometimes earlier.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Conversely, if every exchange still teaches you something new after fifteen, that is a bad sign: you are probably talking to people who are too different, and your hypothesis is too broad to be tested.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "And the day you sign your first customers, none of this work becomes obsolete. It becomes comparable: you hold your hypothesis against what the people who actually paid describe, and the gap between the two is the most valuable information of your first six months.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "You do not need a history to begin. From the very first recorded conversation we pull out the situation described, what has already been tried, the objections and the exact phrasings. Your avatar builds itself during the research phase, and gets corrected with every new call instead of being written once.",
          linkLabel: "See how the client avatar works",
          linkPath: ProtectedPaths.CLIENT_AVATAR,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Do not wait for customers, and do not invent a persona. Write a sentence that could be wrong, take it to ten concerned people, make them describe facts rather than opinions, and keep their words. In a week you have more than a workshop would give you, and above all something that can be corrected.",
        },
      ],
      faq: [
        {
          question: "How do you define your ideal customer with no customers?",
          answer:
            "By replacing customers with conversations. Write a one-sentence hypothesis that could turn out false, take it to five or ten concerned people, and make them describe how they work rather than give an opinion on your idea.",
        },
        {
          question: "How many conversations does validating an avatar take?",
          answer:
            "There is no fixed number, there is a signal: when you predict what the person is about to say before they say it, you have listened enough. It often lands around the eighth. If everything is still new after fifteen, your hypothesis is too broad.",
        },
        {
          question: "Is a marketing persona useful at the start?",
          answer:
            "Rarely. An invented persona with a first name, an age and hobbies contains no line that could turn out false, so it can teach you nothing. A hypothesis phrased to be disproved fills the same role and actually progresses.",
        },
        {
          question: "What questions should you ask in those conversations?",
          answer:
            "Questions that make people describe facts: how do you do it today, tell me about the last time, what have you already tried and why did you stop. Avoid would you pay for this, which everyone answers yes to out of politeness.",
        },
        {
          question: "What do you do with this work once the first customers sign?",
          answer:
            "Compare it. Hold your starting hypothesis against what the people who actually paid describe. The gap between the two is the most useful information of your first months, and it points precisely at what you had misunderstood.",
        },
      ],
    },
  },
};
